import React, { useEffect, useRef } from 'react';
import { getBrandName } from 'utils/brand';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';

import micOffIcon from 'assets/images/mic_off.svg';

import css from './style.scss';

const MIC_PERMISSION_MODAL_TITLE_ID = 'voice-mic-permission-title';

const DEFAULT_TITLE = 'Microphone Access Blocked';

function getDefaultBody() {
  return (
    <>
      {getBrandName()} needs microphone access for Voice Search. Your browser&apos;s permission
      settings are blocking it. Allow microphone access, then try again.
    </>
  );
}

// The blocked-microphone state for voice chat, shared by AI Search and
// PropStream Intelligence (moved here from Search/Header in ATAI-323), and
// also reused for the speech-recognizer `network` error (ATAI-308: same
// portal, focus trap and buttons, with its own title and no body). Kept
// separate from ClickToDial's MicrophoneNotDetected modal, so this copy,
// layout and stylesheet can change without touching the dialer.
//
// Deliberately not `components/base/Modal`: that component adds ESC dismissal,
// outside-click dismissal and a close X, the three affordances this
// forced-choice state omits (both ways out are explicit buttons), and it
// carries no focus management of its own. Focus is handled here instead:
// initial focus on the primary action, Tab cycled between the two buttons, and
// the caller's focus restored on close.
//
// Rendered through a Portal because callers mount it inside containers (the
// search `<header>`, the Intelligence chat) whose ancestors would otherwise
// clip and offset a fixed overlay.
function MicPermissionModal({ onDismiss, onRetry, title, body, zIndex }) {
  const resolvedBody = body === undefined ? getDefaultBody() : body;
  const secondaryRef = useRef(null);
  const primaryRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    primaryRef.current?.focus();
    return () => {
      // Focus would otherwise be left on a removed node, dropping the keyboard
      // user back at the top of the document.
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, []);

  // Only two focusable elements, so the trap is a two-way cycle rather than a
  // general focusable-node query over the subtree.
  function handleKeyDown(e) {
    if (e.key !== 'Tab') return;
    const first = secondaryRef.current;
    const last = primaryRef.current;
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      // backwards off the first control, wrap to the last
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      // forwards off the last control, wrap to the first
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <Portal>
      <div
        className={css.micModalOverlay}
        // A caller that itself sits on a high layer (the Intelligence chat is
        // at z-index 10001) raises the overlay above it.
        style={zIndex != null ? { zIndex } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={MIC_PERMISSION_MODAL_TITLE_ID}
        onKeyDown={handleKeyDown}
      >
        <div className={css.micModal}>
          <div className={css.micModalInner}>
            <img src={micOffIcon} alt="" className={css.micModalIcon} />
            <div className={css.micModalTitle} id={MIC_PERMISSION_MODAL_TITLE_ID}>
              {title}
            </div>
            {resolvedBody != null && resolvedBody !== '' && (
              <div className={css.micModalBody}>{resolvedBody}</div>
            )}
            <div className={css.micModalActions}>
              <button
                type="button"
                className={css.micModalSecondary}
                onClick={onDismiss}
                ref={secondaryRef}
              >
                Dismiss
              </button>
              <button
                type="button"
                className={css.micModalPrimary}
                onClick={onRetry}
                ref={primaryRef}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

MicPermissionModal.propTypes = {
  onDismiss: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  title: PropTypes.string,
  body: PropTypes.node,
  zIndex: PropTypes.number,
};

MicPermissionModal.defaultProps = {
  title: DEFAULT_TITLE,
  body: undefined,
  zIndex: null,
};

export default MicPermissionModal;
